<template>
  <div class="step-container">
    <!-- Header Section -->
    <StepHeader :currentStep="currentStep" :steps="steps" />

    <!-- Content Section -->
    <div class="content-section">
      <div class="wizard-table-container">
        <!-- Table Header -->
        <div class="wizard-table-header">
          <div>{{ lang.W00056 }}</div>
          <div>{{ lang.W00054 }}</div>
          <div>{{ lang.W00016 }}</div>  
        </div>
        <div class="wizard-table-body">
          <div
            v-for="(scenario, index) in localFormData.scenarios"
            :key="`scenario-${scenario.ScenarioId}`"
            class="wizard-table-row"
          >
            <div class="wizard-table-cell">{{ scenario.ScenarioName }}</div>
            <div class="wizard-table-cell">
              <select 
                v-if="localFormData && briefingAttachments" 
                class="wizard-select" 
                :value="getValue(scenario.ScenarioId, 'Briefing')"
                @change="setValue(scenario.ScenarioId, 'Briefing', $event)"
                :disabled="readonly"
              >
                <option :value="null" disabled hidden>{{ lang.W00057 }}</option>
                <option 
                  v-for="file in briefingAttachments" 
                  :value="file.DownloadId" 
                  :key="file.DownloadId"
                >{{ file.FileName }}.{{ file.FileType }}</option>
              </select>
            </div>
            <div class="wizard-table-cell">
              <select 
                v-if="localFormData && surveyAttachments" 
                class="wizard-select" 
                :value="getValue(scenario.ScenarioId, 'Survey')"
                @change="setValue(scenario.ScenarioId, 'Survey', $event)"
                :disabled="readonly"
              >
                <option :value="null" disabled hidden>{{ lang.W00058 }}</option>
                <option 
                  v-for="file in surveyAttachments" 
                  :value="file.DownloadId" 
                  :key="file.DownloadId"
                >{{ file.FileName }}.{{ file.FileType }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- attachment overview -->
      <div class="uploaded-files-section">
        <div>
          <h4><b>{{ lang.W00059 }}</b></h4>
          <div class="files-list">
            <div
              v-for="file in briefingAttachments"
              :key="file.DownloadId"
              class="file-item"
              @click="globals.downloadFile(file.DownloadId)"
            >
              <span class="file-name">{{ file.FileName }}.{{ file.FileType }}</span>
              <i class="material-icons md-24">file_download</i>
            </div>
          </div>
          <div class="upload-btn-container">
            <label class="wizard-btn-dark" for="inputBriefingAttachmentFile" style="margin: 10px;">
              Upload briefing
              <i class="material-icons md-24">file_upload</i>             
            </label>
            <input
              id="inputBriefingAttachmentFile"
              type="file"
              style="display: none"
              accept="application/pdf"
              @change="onInputChange($event, 'Briefing')"
            />          
          </div>
        </div>
        <div>
          <h4><b>{{ lang.W00060 }}</b></h4>
          <div class="files-list">
            <div
              v-for="file in surveyAttachments"
              :key="file.DownloadId"
              class="file-item"
              @click="globals.downloadFile(file.DownloadId)"
            >
              <span class="file-name">{{ file.FileName }}.{{ file.FileType }}</span>
              <i class="material-icons md-24">file_download</i>
            </div>
          </div>
          <div class="upload-btn-container">
            <label class="wizard-btn-dark" for="inputSurveyAttachmentFile" style="margin: 10px;">
              Upload survey
              <i class="material-icons md-24">file_upload</i>             
            </label>
            <input
              id="inputSurveyAttachmentFile"
              type="file"
              style="display: none"
              accept="application/pdf"
              @change="onInputChange($event, 'Survey')"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "../../../styles/audi-steps.css";
import StepHeader from './StepHeader.vue';
import wizardService from "../../../services/WizardService";

export default {
  name: 'Step7',
  components: {
    StepHeader
  },
  props: {
    formData: { type: Object, required: true },
    currentStep: { type: Number, default: 0 },
    steps: { type: Array, required: true },
    readonly: { type: Boolean, default: false }
  },
  data() {
    return {
      localFormData: structuredClone(this.formData),
      briefingAttachments: null,
      surveyAttachments: null,
      lang: {
        W00016: "Survey",
        W00054: "Briefing",
        W00056: "Scenario",
        W00057: "Select a briefing…",
        W00058: "Select a survey…",
        W00059: "Available briefings",
        W00060: "Available surveys",
      }
    }
  },
  watch: {
    "formData": {
      deep: true,
      handler() {
        this.localFormData = structuredClone(this.formData);
      }
    },
    "localFormData": {
      deep: true,
      handler(newVal) {
        this.$emit('update:formData', newVal);
      }
    },
    "globals.selectedLanguage"() {
      this.globals.getLanguageStrings(this);
    },
  },
  mounted() {
    if (this.globals.selectedLanguage) { 
      this.globals.getLanguageStrings(this);
    }
    this.getProjectAttachments();
  },
  methods: {
    onInputChange(e, attachmentType) {
      var files = e.target.files || e.dataTransfer.files;
      if (!files.length || !files[0].name) return;

      let attachmentFileObject = files[0];

      let uploadSuccess = async (data) => {
        let res = await wizardService.addProjectAttachment(this.localFormData.projectGuid, data[0].DownloadId, attachmentType);
        if(res && res.Error) {
          console.error(res.Error);
          globals.showErrorSnackbar("Attachment upload failed.");
        } else {
          this.globals.showSuccessSnackbar("Attachment upload successful.");
          this.getProjectAttachments();
          this.$forceUpdate();
        }
      };
      this.globals.uploadFile(attachmentFileObject, uploadSuccess);
      e.target.value = null;
    },
    setValue(scenarioId, attachmentType, event) {
      if(!this.localFormData.briefing) this.$set(this.localFormData, 'briefing', []);

      if(event && event.target) {
        let res = this.localFormData.briefing.find(e => e.ScenarioId == scenarioId && e.AttachmentType == attachmentType)
        if(res) {
          if(!event.target.value) this.localFormData.briefing = this.localFormData.briefing.filter(e => e != res);
          else res.DownloadId = event.target.value;
        } else {
          this.localFormData.briefing.push({
            "ScenarioId": scenarioId,
            "DownloadId": event.target.value,
            "AttachmentType": attachmentType
          });
        }
      }
    },
    getValue(scenarioId, attachmentType) {
      if(!this.localFormData.briefing) this.$set(this.localFormData, 'briefing', []);

      let res = this.localFormData.briefing.find(e => e.ScenarioId == scenarioId && e.AttachmentType == attachmentType)
      if(res) return res.DownloadId;
      return null;
    },
    async getProjectAttachments() {
      let res = await wizardService.getProjectAttachments(this.localFormData.projectGuid);
      if (res.length > 0) {
        this.briefingAttachments = res.filter(
          (d) => d.AttachmentType === "Briefing"
        );
        this.surveyAttachments = res.filter(
          (d) => d.AttachmentType === "Survey"
        );
      }
    },
    validateStep() {
      return { valid: true, msg: null};
    },
  }
}
</script>

<style lang="css" scoped>
.wizard-table-header {
  grid-template-columns: 1fr 2fr 2fr;
}

.wizard-table-row {
  grid-template-columns: 1fr 2fr 2fr;
}

.uploaded-files-section {
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: 32px;
  gap: 24px;
}

.files-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.file-item {
  background-color: #f1f5f9;
  border: 1px solid #cbd5e1;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 4px;
  cursor: pointer;
}
.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #333333;
}
.file-item:hover .file-name {
  text-decoration: underline;
}
.upload-btn-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// Step7.vue?6aa0d295